import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { AudioAssetDto, ChapterSummary, Project } from "../api/types";
import { orderTakes } from "../lib/takeOrder";
import { useListeningPositions, useSavePosition, useFlagLead } from "../lib/listening";
import { usePickups } from "../lib/pickups";
import type { Pickup, PickupSnapshot } from "../api/types";
import { formatTime } from "../lib/format";

/**
 * Listening on the phone.
 *
 * A chrome-less surface (no sidebar, no desktop shell) for the one thing you
 * want away from a desk: play the book that's already uploaded. It reuses the
 * streaming that already exists — `/audio/{file}/stream` serves HTTP Range and
 * redirects object-stored audio to a presigned bucket URL — so nothing here
 * proxies bytes.
 *
 * Three things are load-bearing and easy to get wrong on a phone:
 *
 * * **One `<audio>` element, forever.** iOS blocks playback that isn't
 *   continuous with a user gesture. Swapping `src` inside the `ended` handler of
 *   an element that is already playing is allowed; remounting a component per
 *   track is not. So the element is created once, imperatively driven, and never
 *   keyed or conditionally rendered.
 * * **Presigned URLs expire mid-listen.** The TTL is an hour and the element
 *   resolves the redirect once, reusing that URL for every later range request.
 *   An hour into a book a seek 403s and playback dies with no useful error, so
 *   `error` re-requests the app URL (cache-busted, to force a fresh redirect)
 *   and resumes where it was.
 * * **Position is server-side.** The point is stopping on a walk and picking up
 *   at the desk, which localStorage cannot do.
 */

const SAVE_EVERY_S = 5;

/** A flag's identity, minted ONCE at creation.
 *
 *  Deliberately carries no timestamp. Pickups upsert on (project, flag_key), so
 *  the key IS the flag — key it by the second it was raised and the first ±1s
 *  nudge writes a SECOND pickup a second away instead of moving the first, and
 *  a few nudges leave a pile of near-duplicates with no way to tell which is
 *  real. The time is just a field on the row. */
function mintFlagKey(projectId: string): string {
  const rand = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
  return `manual:${projectId}:${rand}`;
}

function useProjectDetail(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId],
    enabled: !!projectId,
    queryFn: () => api<Project & { chapters: ChapterSummary[] }>(`/api/projects/${projectId}`),
  });
}

interface ScriptToken { text: string; sep: string; audio_time: number | null; page: number | null }

/** The chapter's words with per-word audio times, for follow-along reading.
 *  409 ⇒ not proofed, so there is nothing to follow — surfaced as `unproofed`
 *  rather than an error, because it is a normal state for a fresh take. */
function useChapterScript(projectId: string | undefined, chapterId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["chapter-script", projectId, chapterId],
    enabled: !!projectId && !!chapterId && enabled,
    staleTime: 600_000,
    retry: false,
    queryFn: () =>
      api<{ tokens: ScriptToken[] }>(
        `/api/modules/proofing/projects/${projectId}/chapters/${chapterId}/script`,
      ),
  });
}

function useAudioAssets(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "audio"],
    enabled: !!projectId,
    queryFn: () => api<{ assets: AudioAssetDto[] }>(`/api/projects/${projectId}/audio`).then((r) => r.assets),
  });
}

/** "Northanger Abbey" → "NA"; one word → its first two letters. */
function bookInitials(name: string): string {
  const parts = (name || "?").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function bookColour(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 46% 44%)`;
}

// ---------------------------------------------------------------------------
// Library
// ---------------------------------------------------------------------------

export function ListenLibrary({ onOpenBook, inDrawer }: {
  /** Supplied by the drawer, which selects a book in state rather than by URL.
   *  Absent on the standalone route, where opening a book IS a navigation. */
  onOpenBook?: (projectId: string) => void;
  inDrawer?: boolean;
} = {}) {
  const navigate = useNavigate();
  const openBook = onOpenBook ?? ((id: string) => navigate(`/listen/${id}`));
  const { data: projects, isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: () => api<{ projects: Project[] }>("/api/projects").then((r) => r.projects),
  });
  const { data: positions } = useListeningPositions();

  // Newest position per project — "where I am in this book".
  const whereIAm = useMemo(() => {
    const m = new Map<string, { file: string; at: number }>();
    (positions || []).forEach((p) => {
      if (!m.has(p.project_id)) m.set(p.project_id, { file: p.audio_filename, at: p.position_s });
    });
    return m;
  }, [positions]);

  const books = (projects || []).filter((p) => !p.archived && p.n_audio > 0);

  return (
    <div className={`lst${inDrawer ? " lst-embedded" : ""}`}>
      <header className="lst-nav">
        <div className="lst-navtext">
          <h1>Your books</h1>
          <span>{books.length} with audio</span>
        </div>
      </header>
      <div className="lst-body">
        {isLoading && <p className="lst-empty">Loading…</p>}
        {!isLoading && !books.length && (
          <p className="lst-empty">Nothing uploaded yet. Add audio to a project and it'll appear here.</p>
        )}
        <div className="lst-lib">
          {books.map((p) => {
            const pos = whereIAm.get(p.id);
            return (
              <button key={p.id} className="lst-book" onClick={() => openBook(p.id)}>
                <span className="lst-cover" style={{ background: bookColour(p.name) }}>{bookInitials(p.name)}</span>
                <span className="lst-bt">
                  <span className="lst-bn">{p.name}</span>
                  <span className="lst-bm">
                    {pos ? `${pos.file} · ${formatTime(pos.at)} in` : `${p.n_audio} file${p.n_audio === 1 ? "" : "s"} · not started`}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Tags worth reaching for by thumb. They go in the note as plain text, so they
 *  travel into the pickup's comment and are searchable everywhere it appears —
 *  no new field, no schema, and the desktop reads them without knowing. */
const FLAG_TAGS = ["#misread", "#missing", "#added", "#pacing", "#noise", "#defect"];

function FlagEditor({ flag, showClose, onClose, onNudge, onHear, onComment }: {
  flag: Pickup;
  showClose: boolean;
  onClose: () => void;
  onNudge: (delta: number) => void;
  onHear: () => void;
  onComment: (comment: string) => void;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  // The note stays UNCONTROLLED (defaultValue + a ref): typing must not push a
  // render through the player on every keystroke, and the tag buttons need to
  // read and rewrite the live value anyway.
  const [tags, setTags] = useState<string[]>(() => tagsIn(flag.comment));

  // What is in the box, and what has already been written. Both in refs, and
  // `live` is maintained WITHOUT reading the DOM, because React has already
  // detached `taRef` by the time an unmount cleanup runs.
  const live = useRef(flag.comment);
  const sent = useRef(flag.comment);
  const commit = (v: string) => {
    if (v === sent.current) return;
    sent.current = v;
    onComment(v);
  };
  // The card no longer only closes via "✓ Done" — raising the NEXT flag closes
  // it too. Unmount does not fire blur, so a note typed and not blurred would
  // be lost exactly when you are flagging fast, which is when you can least
  // afford to stop and check.
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => () => commitRef.current(live.current), []);

  const toggleTag = (tag: string) => {
    const ta = taRef.current;
    if (!ta) return;
    const has = tagsIn(ta.value).includes(tag);
    // Strip on the way out so a mis-tap is one tap to undo, and re-add at the
    // end so the tags stay together rather than scattered through the note.
    const body = ta.value
      .split(/\s+/)
      .filter((w) => w && w.toLowerCase() !== tag)
      .join(" ")
      .trim();
    ta.value = has ? body : (body ? `${body} ${tag}` : tag);
    live.current = ta.value;
    setTags(tagsIn(ta.value));
    commit(ta.value);
  };

  return (
    <div className="lst-fcard">
      <div className="lst-fh">
        <span className="lst-ft">{formatTime(flag.start_time ?? 0)}</span>
        <span className="lst-fs">{showClose ? "flagged · pickup created" : flag.shown_filename || flag.audio_filename}</span>
        {showClose && (
          // Commits whatever is in the box before closing. Relying on the
          // textarea's blur would be a race with the unmount — and the anxiety
          // this button was causing was precisely "will my note survive this?".
          <button className="lst-fx" onClick={() => {
            commit(taRef.current?.value ?? live.current);
            onClose();
          }}>✓ Done</button>
        )}
      </div>
      <div className="lst-fnudge">
        <button className="lst-nb" onClick={() => onNudge(-1)}>−1s</button>
        <button className="lst-nb" onClick={onHear}>▶ hear it</button>
        <button className="lst-nb" onClick={() => onNudge(1)}>+1s</button>
      </div>
      <textarea ref={taRef} className="lst-note" rows={2} defaultValue={flag.comment}
        placeholder="Add a note — or leave it and keep listening"
        onChange={(e) => { live.current = e.target.value; }}
        onBlur={(e) => commit(e.target.value)} />
      <div className="lst-tags">
        {FLAG_TAGS.map((tag) => (
          <button key={tag} className={`lst-tag${tags.includes(tag) ? " on" : ""}`}
            onClick={() => toggleTag(tag)}>{tag}</button>
        ))}
      </div>
    </div>
  );
}

/** The tags present in a note, lower-cased for comparison. */
function tagsIn(text: string): string[] {
  return (text || "").split(/\s+/).map((w) => w.toLowerCase()).filter((w) => FLAG_TAGS.includes(w));
}

// ---------------------------------------------------------------------------
// One book: queue + player
// ---------------------------------------------------------------------------

export function ListenBook({ projectId: propId, onBack, inDrawer }: {
  projectId?: string;
  onBack?: () => void;
  inDrawer?: boolean;
} = {}) {
  const params = useParams();
  const projectId = propId ?? params.projectId;
  const navigate = useNavigate();
  const goBack = onBack ?? (() => navigate("/listen"));
  const { data: project } = useProjectDetail(projectId);
  const { data: assets } = useAudioAssets(projectId);
  const { data: positions } = useListeningPositions();
  const savePos = useSavePosition();
  const pickups = usePickups(projectId || "");
  const { lead } = useFlagLead();

  const [view, setView] = useState<"files" | "player" | "flags">("files");
  // Where the flags list was opened FROM. Back returns there rather than to a
  // fixed screen: landing on the file list after checking a flag reads as "pick
  // something to listen to next" when you were mid-chapter and not done with it.
  const [flagsFrom, setFlagsFrom] = useState<"files" | "player">("player");
  // The flag whose card is open on the player. Editing happens where you are —
  // a flag is one tap BECAUSE stopping loses the passage, so sending you to a
  // list to write the note would hand back the cost the tap just saved.
  const [editing, setEditing] = useState<string | null>(null);
  // Reading mode: the player collapses to a strip and the manuscript takes the
  // screen, so you can follow the words as they are spoken.
  const [reading, setReading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [speed, setSpeed] = useState(1);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastSaved = useRef(0);
  // Where to resume to after a recovery — read inside the `error` handler, which
  // fires long after the render that set it.
  const resumeTo = useRef(0);

  const files = useMemo(
    () => orderTakes(project?.chapters || [], assets || []),
    [project, assets],
  );
  // The name each file is known by (a corrected delivery reads as the file it
  // replaced); `files` stay storage names, which is what streams and saves.
  const shownByFile = useMemo(
    () => new Map((assets || []).map((a) => [a.filename, a.display_name || a.filename])),
    [assets],
  );
  const durByFile = useMemo(() => {
    const m = new Map<string, number>();
    (assets || []).forEach((a) => { if (a.duration_seconds) m.set(a.filename, a.duration_seconds); });
    return m;
  }, [assets]);
  const posByFile = useMemo(() => {
    const m = new Map<string, number>();
    (positions || []).filter((p) => p.project_id === projectId)
      .forEach((p) => { if (!m.has(p.audio_filename)) m.set(p.audio_filename, p.position_s); });
    return m;
  }, [positions, projectId]);

  const file = files[idx];

  /** Flags raised by ear, newest first — the phone's own, not engine findings. */
  const myFlags = useMemo(
    () => pickups.list
      .filter((f) => f.flag_type === "manual")
      .slice()
      .sort((a, b) => (a.audio_filename || "").localeCompare(b.audio_filename || "")
        || (a.start_time ?? 0) - (b.start_time ?? 0)),
    [pickups.list],
  );
  const chapterIdFor = useCallback((fn: string) => {
    const c = (project?.chapters || []).find((x) => x.audio_filename === fn);
    return c ? { id: c.id, name: c.name } : { id: null, name: null };
  }, [project]);

  /** Re-send a flag WHOLE. The pickup endpoint replaces every column from the
   *  body, so a "just the comment" patch would blank the timestamp and file. */
  const writeFlag = useCallback(
    (snap: PickupSnapshot, opts?: { onError?: () => void }) => pickups.pick(snap, opts),
    [pickups],
  );

  /** The "that landed" confirmation. Held as one object so a second flag
   *  REPLACES the first rather than stacking — a queue of notices on a phone is
   *  itself the thing getting in the way. `n` rises on every press so the
   *  animation restarts even when the toast never left the screen. */
  const [flash, setFlash] = useState<
    { n: number; key: string; at: number; snap: PickupSnapshot; failed: boolean } | null
  >(null);
  const flashN = useRef(0);
  const flashTimer = useRef<number | null>(null);
  const clearFlashTimer = () => {
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
    flashTimer.current = null;
  };
  useEffect(() => clearFlashTimer, []);

  /** Write a flag and say so — truthfully. The cache update is optimistic, so a
   *  failed POST would otherwise leave a cheerful toast as the only trace of a
   *  flag that the next refetch quietly removes. */
  const sendFlag = useCallback((snap: PickupSnapshot) => {
    clearFlashTimer();
    flashN.current += 1;
    setFlash({ n: flashN.current, key: snap.flag_key, at: snap.start_time ?? 0, snap, failed: false });
    flashTimer.current = window.setTimeout(() => setFlash(null), 4200);
    writeFlag(snap, {
      onError: () => {
        clearFlashTimer();  // a failure waits to be dealt with; it does not time out
        setFlash((f) => (f && f.key === snap.flag_key ? { ...f, failed: true } : f));
      },
    });
  }, [writeFlag]);

  // A tap on the pad flags; a horizontal drag across it steps. Distinguished on
  // release rather than by a gesture library: the only thing that has to be
  // true is that a swipe does not also fire the click underneath it.
  const padFrom = useRef<{ x: number; y: number } | null>(null);
  const padSwiped = useRef(false);

  const raiseFlag = useCallback(() => {
    const el = audioRef.current;
    if (!el || !file || !projectId) return;
    const at = Math.max(0, el.currentTime - lead);
    const ch = chapterIdFor(file);
    const key = mintFlagKey(projectId);
    // The card no longer opens itself. Flagging two moments ten seconds apart
    // used to mean the artwork collapsing to a thumbnail, the button jumping up
    // the column, and the second tap landing on the first flag's note box. The
    // toast confirms without moving anything, and the note is one tap from it.
    setEditing(null);
    navigator.vibrate?.(12);  // Android only; iOS Safari has no Vibration API.
    sendFlag({
      flag_key: key, chapter_id: ch.id, chapter_name: ch.name, audio_filename: file,
      flag_type: "manual", category: "manual", page: null,
      script_text: "", heard_text: "", context_before: "", context_after: "",
      title: "", comment: "", start_time: at, end_time: null,
    });
  }, [file, projectId, lead, chapterIdFor, sendFlag]);

  const srcFor = useCallback(
    (fn: string) => `/api/projects/${projectId}/audio/${encodeURIComponent(fn)}/stream`,
    [projectId],
  );

  /** Point the ONE element at a file and (optionally) play. Never remounts. */
  const load = useCallback((i: number, at: number, autoplay: boolean) => {
    const el = audioRef.current;
    const fn = files[i];
    if (!el || !fn) return;
    setIdx(i);
    resumeTo.current = at;
    el.src = srcFor(fn);
    el.load();
    const seek = () => {
      if (at > 0) el.currentTime = at;
      el.removeEventListener("loadedmetadata", seek);
    };
    el.addEventListener("loadedmetadata", seek);
    if (autoplay) el.play().catch(() => setPlaying(false));
  }, [files, srcFor]);

  // Media Session — the lock screen, AirPods and the car. Without it the phone
  // knows something is playing but not what, and the hardware buttons are inert.
  useEffect(() => {
    const ms = (navigator as Navigator & { mediaSession?: MediaSession }).mediaSession;
    if (!ms || !file || !project) return;
    ms.metadata = new MediaMetadata({
      title: file,
      artist: project.name,
      album: "Mockingbird",
      artwork: [{ src: "/app-icon.png", sizes: "512x512", type: "image/png" }],
    });
    ms.setActionHandler("play", () => audioRef.current?.play());
    ms.setActionHandler("pause", () => audioRef.current?.pause());
    ms.setActionHandler("seekbackward", () => skip(-30));
    ms.setActionHandler("seekforward", () => skip(30));
    ms.setActionHandler("previoustrack", idx > 0 ? () => load(idx - 1, 0, true) : null);
    ms.setActionHandler("nexttrack", idx < files.length - 1 ? () => load(idx + 1, 0, true) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, project, idx, files.length, load]);

  /** The one that matters. "What was that again" is a sentence, not a word, so
   *  the quick step is ten seconds: five lands mid-phrase and thirty overshoots
   *  the thing you were checking. */
  const QUICK = 10;

  function skip(delta: number) {
    const el = audioRef.current;
    if (el) el.currentTime = Math.max(0, Math.min(el.duration || 0, el.currentTime + delta));
  }

  function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) { if (!el.src) load(idx, posByFile.get(files[idx]) ?? 0, true); else el.play(); }
    else el.pause();
  }

  // Element wiring. Bound once; everything else drives the element imperatively.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      setCur(el.currentTime);
      resumeTo.current = el.currentTime;
      if (projectId && files[idx] && el.currentTime - lastSaved.current > SAVE_EVERY_S) {
        lastSaved.current = el.currentTime;
        savePos.mutate({ projectId, audio_filename: files[idx], position_s: el.currentTime });
      }
    };
    const onMeta = () => setDur(el.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => {
      setPlaying(false);
      if (projectId && files[idx]) {
        savePos.mutate({ projectId, audio_filename: files[idx], position_s: el.currentTime });
      }
    };
    const onEnded = () => {
      // Inside the handler of an element that was already playing, so iOS allows
      // the next track to start without a fresh gesture.
      if (idx < files.length - 1) load(idx + 1, 0, true);
      else setPlaying(false);
    };
    const onError = () => {
      // Most likely the presigned URL behind the redirect expired. Re-request the
      // APP url with a cache-buster so the browser takes a fresh 307 rather than
      // reusing the dead bucket URL, then drop back where we were.
      if (!el.src || !files[idx]) return;
      const at = resumeTo.current;
      el.src = `${srcFor(files[idx])}?r=${Date.now()}`;
      el.load();
      const seek = () => {
        el.currentTime = at;
        el.removeEventListener("loadedmetadata", seek);
        el.play().catch(() => setPlaying(false));
      };
      el.addEventListener("loadedmetadata", seek);
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onError);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onError);
    };
  }, [idx, files, projectId, load, srcFor, savePos]);

  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = speed; }, [speed]);

  const resumeFile = useMemo(() => {
    const p = (positions || []).filter((x) => x.project_id === projectId)[0];
    if (!p) return null;
    const i = files.indexOf(p.audio_filename);
    return i >= 0 ? { i, at: p.position_s, name: p.audio_filename } : null;
  }, [positions, projectId, files]);

  /** Move a flag ±1s, or rewrite its note. The pickup endpoint replaces every
   *  column from the body, so both send the WHOLE flag — a partial write would
   *  blank the timestamp and the filename. */
  const rewrite = (key: string, patch: Partial<PickupSnapshot>) => {
    const f = myFlags.find((x) => x.flag_key === key);
    if (!f) return;
    pickups.pick({
      flag_key: f.flag_key, chapter_id: f.chapter_id, chapter_name: f.chapter_name,
      audio_filename: f.audio_filename, flag_type: f.flag_type, category: f.category,
      page: f.page, script_text: f.script_text, heard_text: f.heard_text,
      context_before: f.context_before, context_after: f.context_after,
      title: f.title, comment: f.comment, start_time: f.start_time, end_time: f.end_time,
      ...patch,
    });
  };
  const nudge = (key: string, delta: number) => {
    const f = myFlags.find((x) => x.flag_key === key);
    if (f) rewrite(key, { start_time: Math.max(0, (f.start_time ?? 0) + delta) });
  };
  /** Jump to the flag AND play. Only moving the playhead reads as a dead
   *  button: nothing is heard, so nothing appears to have happened — which is
   *  the opposite of what "hear it" promises. */
  const hearIt = (f: { audio_filename: string | null; start_time: number | null }) => {
    const i = files.indexOf(f.audio_filename || "");
    if (i < 0) return;
    const at = Math.max(0, f.start_time ?? 0);
    if (i !== idx) { load(i, at, true); return; }
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = at;
    el.play().catch(() => setPlaying(false));
  };

  const flagEditor = (key: string, showClose: boolean) => {
    const f = myFlags.find((x) => x.flag_key === key);
    if (!f) return null;
    return (
      <FlagEditor key={key} flag={f} showClose={showClose}
        onClose={() => setEditing(null)}
        onNudge={(d) => nudge(key, d)}
        onHear={() => hearIt(f)}
        onComment={(c) => rewrite(key, { comment: c })} />
    );
  };

  /** Docked, not in the flow: the dock is a zero-height flex item, so the
   *  notice paints over the top of the screen and NOTHING below it moves. It
   *  sits under the header rather than at the bottom because the bottom is
   *  where the flag button lives, in both views. */
  const flagToast = () => (
    <div className="lst-dock">
      {flash && (
        <div key={flash.n} className={`lst-toast${flash.failed ? " bad" : ""}`}
          role="status" aria-live="polite">
          <span className="lst-tk" aria-hidden="true">⚑</span>
          <span className="lst-tt">
            {flash.failed ? "That flag didn't save" : `Flagged at ${formatTime(flash.at)}`}
          </span>
          {flash.failed ? (
            <button className="lst-tb" onClick={() => sendFlag(flash.snap)}>Retry</button>
          ) : (
            <button className="lst-tb" onClick={() => {
              clearFlashTimer();
              setFlash(null);
              setEditing(flash.key);
            }}>Add note</button>
          )}
        </div>
      )}
    </div>
  );

  const miniPlayer = () => (
    <button className="lst-mini" onClick={() => setView("player")}>
      <span className="lst-mc" style={{ background: cover }}>{bookInitials(project?.name || "")}</span>
      <span className="lst-mt">
        <span className="lst-mn">{shownByFile.get(file) ?? file}</span>
        <span className="lst-ms">playing · {formatTime(cur)}</span>
      </span>
      <span className="lst-mp" onClick={(e) => { e.stopPropagation(); toggle(); }}>❚❚</span>
    </button>
  );

  const chapterForFile = useMemo(
    () => (project?.chapters || []).find((c) => c.audio_filename === file) || null,
    [project, file],
  );
  // Decided from the chapter's PROOF STATE, not from the script request. The
  // request only runs once reading is on, so keying off its error left the
  // toggle looking available on an unproofed take right until you tapped it —
  // the opposite of stating the limit. The state is already loaded.
  const canRead = chapterForFile?.state === "proofed";
  const script = useChapterScript(projectId, chapterForFile?.id ?? null, reading && canRead);

  // The word being spoken: the last token whose time has passed. Binary search
  // because this runs on every timeupdate and a chapter is tens of thousands of
  // tokens — a linear scan would burn a phone battery to find the same word.
  const timed = useMemo(() => {
    const toks = script.data?.tokens || [];
    const idxs: number[] = [];
    const times: number[] = [];
    toks.forEach((tk, i) => { if (tk.audio_time != null) { idxs.push(i); times.push(tk.audio_time); } });
    return { idxs, times };
  }, [script.data]);

  const activeToken = useMemo(() => {
    const { idxs, times } = timed;
    if (!times.length) return -1;
    let lo = 0, hi = times.length - 1, ans = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (times[mid] <= cur) { ans = mid; lo = mid + 1; } else hi = mid - 1;
    }
    return ans >= 0 ? idxs[ans] : -1;
  }, [timed, cur]);

  // Auto-scroll, but never fight the reader: any manual scroll parks it for a
  // few seconds so you can read ahead (or back) without being yanked.
  const scriptRef = useRef<HTMLDivElement | null>(null);
  const scrolledAt = useRef(0);
  useEffect(() => {
    if (!reading || activeToken < 0) return;
    if (Date.now() - scrolledAt.current < 4000) return;
    const el = scriptRef.current?.querySelector(`[data-tok="${activeToken}"]`);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [activeToken, reading]);

  const total = dur || durByFile.get(file || "") || 0;
  const cover = project ? bookColour(project.name) : "#379DAE";

  return (
    <div className={`lst${inDrawer ? " lst-embedded" : ""}`}>
      {/* The single element. Rendered unconditionally, outside every branch. */}
      <audio ref={audioRef} preload="metadata" playsInline />

      {view === "player" && reading ? (
        <>
          <header className="lst-nav">
            <button className="lst-back" onClick={() => setReading(false)} aria-label="Back to the player">‹</button>
            <div className="lst-navtext">
              <h1>{chapterForFile?.name || file}</h1>
              <span>{script.isLoading ? "loading the text…" : "following as it plays"}</span>
            </div>
          </header>
          {flagToast()}
          <div className="lst-script" ref={scriptRef}
            onScroll={() => { scrolledAt.current = Date.now(); }}>
            {script.isLoading && <p className="lst-empty">Loading the manuscript…</p>}
            {script.data?.tokens.map((tk, i) => (
              <span key={i} data-tok={i}
                className={`lst-tok${i === activeToken ? " on" : ""}${i < activeToken ? " past" : ""}`}
                onClick={() => {
                  const el = audioRef.current;
                  if (el && tk.audio_time != null) el.currentTime = tk.audio_time;
                }}>
                {tk.text}{tk.sep ?? " "}
              </span>
            ))}
          </div>
          {/* The player collapses to a strip: everything you need mid-read, and
              the flag stays one tap away because that is when you notice. */}
          {/* Same two scales as the main transport, same order: ±30 to move
              about the take, ±5 inboard to land on the line you just read. */}
          <div className="lst-readbar">
            <button className="lst-rb" onClick={() => skip(-30)}>−30</button>
            <button className="lst-rb sm" onClick={() => skip(-5)}>−5</button>
            <button className="lst-rb pp" onClick={toggle}>{playing ? "❚❚" : "▶"}</button>
            <button className="lst-rb sm" onClick={() => skip(5)}>+5</button>
            <button className="lst-rb" onClick={() => skip(30)}>+30</button>
            <span className="lst-rt2">{formatTime(cur)}</span>
            <span className="lst-rbwrap">
              <button className="lst-rb flag" onClick={raiseFlag} aria-label="Flag this moment">⚑</button>
              {flash && <span key={flash.n} className="lst-burst sm" aria-hidden="true" />}
            </span>
          </div>
          {editing && <div className="lst-readcard">{flagEditor(editing, true)}</div>}
        </>
      ) : view === "flags" ? (
        <>
          <header className="lst-nav">
            <button className="lst-back" onClick={() => setView(flagsFrom)} aria-label="Back">‹</button>
            <div className="lst-navtext">
              <h1>Flags</h1>
              <span>{myFlags.length} on this book · already pickups</span>
            </div>
          </header>
          <div className="lst-body">
            {!myFlags.length && (
              <p className="lst-empty">Nothing flagged yet. Tap ⚑ while you listen — the note can wait.</p>
            )}
            {myFlags.map((f) => flagEditor(f.flag_key, false))}
          </div>
          {/* The audio never stopped because you opened a list. */}
          {playing && miniPlayer()}
        </>
      ) : view === "files" ? (
        <>
          <header className="lst-nav">
            <button className="lst-back" onClick={goBack} aria-label="All books">‹</button>
            <div className="lst-navtext">
              <h1>{project?.name || "Loading…"}</h1>
              <span>{files.length} file{files.length === 1 ? "" : "s"}</span>
            </div>
            {myFlags.length > 0 && (
              <button className="lst-pill" onClick={() => { setFlagsFrom("files"); setView("flags"); }}>
                ⚑ {myFlags.length}
              </button>
            )}
          </header>
          <div className="lst-body">
            {resumeFile && (
              <button className="lst-resume" onClick={() => { load(resumeFile.i, resumeFile.at, true); setView("player"); }}>
                <span className="lst-rico">▶</span>
                <span className="lst-rt">
                  Resume
                  <span className="lst-rs">{resumeFile.name} · {formatTime(resumeFile.at)}</span>
                </span>
              </button>
            )}
            <div className="lst-files">
              {files.map((fn, i) => (
                <button key={fn} className={`lst-file${i === idx && playing ? " on" : ""}`}
                  onClick={() => { load(i, posByFile.get(fn) ?? 0, true); setView("player"); }}>
                  <span className="lst-fi">{String(i + 1).padStart(2, "0")}</span>
                  <span className="lst-fn">{shownByFile.get(fn) ?? fn}</span>
                  <span className="lst-fd">{durByFile.get(fn) ? formatTime(durByFile.get(fn)!) : ""}</span>
                </button>
              ))}
            </div>
          </div>
          {playing && miniPlayer()}
        </>
      ) : (
        <>
          <header className="lst-nav">
            <button className="lst-back" onClick={() => setView("files")} aria-label="Back to the file list">‹</button>
            <div className="lst-navtext">
              <h1>{project?.name}</h1>
              <span>Now playing</span>
            </div>
            {myFlags.length > 0 && (
              <button className="lst-pill" onClick={() => { setFlagsFrom("player"); setView("flags"); }}>
                ⚑ <span key={myFlags.length} className="lst-pilln">{myFlags.length}</span>
              </button>
            )}
          </header>
          <div className="lst-player">
            {/* The biggest thing on the screen is the thing you actually do.
                This was a decorative square; a third of the display spent on
                artwork nobody looks at, while the action it exists for was the
                sixth row down and needed aiming. Now the whole block is the
                flag: you are listening, not looking, and a target this size is
                one you hit without moving your eyes off nothing in particular.

                It doubles as the clock, because the question the timecode
                answers - "what moment am I about to flag" - is the same one. */}
            <div className={`lst-pad${editing ? " mini" : ""}${flash ? " lit" : ""}`}
              style={{ ["--pad-cover" as string]: cover }}
              role="button" tabIndex={0} aria-label="Flag this moment"
              onPointerDown={(e) => { padFrom.current = { x: e.clientX, y: e.clientY }; padSwiped.current = false; }}
              onPointerUp={(e) => {
                const from = padFrom.current;
                padFrom.current = null;
                if (!from) return;
                const dx = e.clientX - from.x, dy = e.clientY - from.y;
                // Horizontal, and decisively so: a thumb resting on a phone
                // wanders a few pixels, and that must not scrub.
                if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy)) {
                  padSwiped.current = true;
                  skip(dx > 0 ? QUICK : -QUICK);
                }
              }}
              onClick={() => {
                if (padSwiped.current) { padSwiped.current = false; return; }
                if (file) raiseFlag();
              }}
              onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && file) { e.preventDefault(); raiseFlag(); } }}
            >
              {flash ? (
                /* Confirmed where the thumb already is. The note used to be
                   offered in a toast at the far end of the screen - a reach
                   away from the tap that earned it. */
                <div className="lst-pad-done" onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                  <span className="lst-pad-said">
                    {flash.failed ? "That flag didn't save" : `Flagged ${formatTime(flash.at)}`}
                  </span>
                  <div className="lst-pad-acts">
                    {flash.failed ? (
                      <button className="lst-pad-act primary" onClick={() => sendFlag(flash.snap)}>Retry</button>
                    ) : (
                      <>
                        <button className="lst-pad-act primary" onClick={() => {
                          clearFlashTimer(); setFlash(null); setEditing(flash.key);
                        }}>Add note</button>
                        <button className="lst-pad-act" onClick={() => {
                          clearFlashTimer(); pickups.unpick(flash.key); setFlash(null);
                        }}>Undo</button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <span className="lst-pad-time">{formatTime(cur)}</span>
                  <span className="lst-pad-hint">Tap anywhere to flag · swipe to step {QUICK}s</span>
                </>
              )}
            </div>
            <div className="lst-title">{shownByFile.get(file) ?? file}</div>
            <div className="lst-sub">{project?.name}</div>

            <input className="lst-track" type="range" min={0} max={Math.max(1, total)} step={1} value={Math.min(cur, total || 1)}
              onChange={(e) => { const el = audioRef.current; if (el) el.currentTime = Number(e.target.value); }}
              aria-label="Position" />
            <div className="lst-times"><span>{formatTime(cur)}</span><span>−{formatTime(Math.max(0, total - cur))}</span></div>

            {/* Sized by how often each is pressed, not by media-player habit.
                Listening through a take you press play ONCE and go back twenty
                times - so the back step is the hero and play is not. They sit
                at the edges rather than clustered in the middle, because the
                edge of the screen is a landmark a thumb can find without
                looking and the centre of a row of five is not. */}
            <div className="lst-ctrls">
              <button className="lst-sk hero" onClick={() => skip(-QUICK)}
                aria-label={`Back ${QUICK} seconds`}>−{QUICK}</button>
              <button className="lst-pp" onClick={toggle}
                aria-label={playing ? "Pause" : "Play"}>{playing ? "❚❚" : "▶"}</button>
              <button className="lst-sk fwd" onClick={() => skip(QUICK)}
                aria-label={`Forward ${QUICK} seconds`}>+{QUICK}</button>
            </div>
            <div className="lst-row2">
              {/* The coarse step is for moving ABOUT the take, which the scrub
                  bar above already does - so it keeps its place, one rank down. */}
              <button className="lst-chip" onClick={() => skip(-30)}>−30</button>
              <button className="lst-chip" onClick={() => skip(30)}>+30</button>
              <button className="lst-chip" disabled={idx <= 0}
                onClick={() => load(idx - 1, 0, true)}>‹ Prev</button>
              <button className="lst-chip" onClick={() => setSpeed(speed === 2 ? 1 : Math.round((speed + 0.25) * 100) / 100)}>
                {speed}×
              </button>
              <button className="lst-chip" title={canRead
                  ? "Follow the manuscript as it plays"
                  : "This take hasn't been proofed yet, so there's no text to follow"}
                disabled={!canRead}
                onClick={() => setReading(true)}>Manuscript</button>
              <button className="lst-chip" disabled={idx >= files.length - 1}
                onClick={() => load(idx + 1, 0, true)}>Next ›</button>
            </div>

            {editing && flagEditor(editing, true)}
          </div>
        </>
      )}
    </div>
  );
}
